import { format } from "date-fns";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { type ZodType, z } from "zod";

export const trimmedNullable = (
	schema: ZodType<string>,
): ZodType<string | null, z.ZodTypeDef, string> =>
	schema.transform((val) => {
		const trimmed = val.trim();
		return trimmed === "" ? null : trimmed;
	});

const ZIP_PATTERN = /^\d{3} ?\d{2}$/;

export const optionalZipCode = (
	formatMessage: string,
): ZodType<string | null, z.ZodTypeDef, string> =>
	trimmedNullable(
		z
			.string()
			.refine((val) => val === "" || ZIP_PATTERN.test(val), formatMessage),
	);

type PositiveNumberMessages = {
	positiveMessage: string;
	maxMessage: string;
};

export const optionalPositiveInteger = (
	max: number,
	messages: PositiveNumberMessages,
): ZodType<number | null, z.ZodTypeDef, number | null | undefined> =>
	z
		.number()
		.int(messages.positiveMessage)
		.min(1, messages.positiveMessage)
		.max(max, messages.maxMessage)
		.nullish()
		.transform((val) => val ?? null);

export const optionalPositiveFloat = (
	max: number,
	messages: PositiveNumberMessages,
): ZodType<number | null, z.ZodTypeDef, number | null | undefined> =>
	z
		.number()
		.gt(0, messages.positiveMessage)
		.max(max, messages.maxMessage)
		.nullish()
		.transform((val) => val ?? null);

export const optionalPastDateAsIsoString = (
	notInFutureMessage: string,
): ZodType<string | null, z.ZodTypeDef, Date | null> =>
	z
		.date()
		.nullable()
		.refine(
			(val) => val == null || val.getTime() <= Date.now(),
			notInFutureMessage,
		)
		.transform((val) => (val ? format(val, DateFormat.Iso) : null));

export const optionalDateAsIsoString = (): ZodType<
	string | null,
	z.ZodTypeDef,
	Date | null
> =>
	z
		.date()
		.nullable()
		.transform((val) => (val ? format(val, DateFormat.Iso) : null));

type DateOrderMessages = {
	startBeforeSigned: string;
	endBeforeSigned: string;
	endBeforeStart: string;
	fixationEndBeforeStart?: string;
};

type DateTrio = {
	signedAt: string | null;
	startAt: string | null;
	endAt: string | null;
	fixationEndDate?: string | null;
};

export const refineDateOrder =
	(
		messages: DateOrderMessages,
		fixationEndPath: (string | number)[] = ["fixationEndDate"],
	) =>
	(data: DateTrio, ctx: z.RefinementCtx): void => {
		const { signedAt, startAt, endAt, fixationEndDate } = data;

		if (signedAt && startAt && startAt < signedAt) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["startAt"],
				message: messages.startBeforeSigned,
			});
		}
		if (signedAt && endAt && endAt < signedAt) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["endAt"],
				message: messages.endBeforeSigned,
			});
		}
		if (startAt && endAt && endAt < startAt) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["endAt"],
				message: messages.endBeforeStart,
			});
		}
		if (
			messages.fixationEndBeforeStart &&
			startAt &&
			fixationEndDate &&
			fixationEndDate < startAt
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: fixationEndPath,
				message: messages.fixationEndBeforeStart,
			});
		}
	};

export const optionalPastDateAsMonthYearString = (
	notInFutureMessage: string,
): ZodType<string | null, z.ZodTypeDef, Date | null> =>
	z
		.date()
		.nullable()
		.refine(
			(val) => val == null || val.getTime() <= Date.now(),
			notInFutureMessage,
		)
		.transform((val) => (val ? format(val, DateFormat.MonthYear) : null));
